import { color } from '@ahamove/design-tokens';
import Box from '@ahamove/web-components/lib/Box';
import type { SliderProps } from '@ahamove/web-components/lib/Slider';
import Slider, { sliderClasses } from '@ahamove/web-components/lib/Slider';
import dayjs from 'dayjs';
import React from 'react';
import { useRentTypeStore } from '@/global-stores/useRentTypeStore';
import { getValidStartTime } from '@/lib/common';
import { DEFAULT_TIME_RANGE_OBJ } from '@/lib/constants';

type TimeSliderProps = {
  isFlexibleDate?: boolean;
  minHourConfig?: number;
  timeConfig?: number;
  type?: 'start' | 'end';
  startDate?: dayjs.Dayjs;
  endDate?: dayjs.Dayjs;
  maxSelectableDate?: dayjs.Dayjs;
  value: number;
  onChange: (value: number) => void;
} & Omit<SliderProps, 'onChange'>;

const getSeconds = (value: number) => {
  const time = dayjs.unix(value);
  const hours = time.hour();
  const minutes = time.minute();
  const seconds = time.second();
  const totalSeconds = hours * 3600 + minutes * 60 + seconds;

  return totalSeconds;
};

export default function TimeSlider({
  isFlexibleDate = false,
  minHourConfig = 0,
  timeConfig = 0,
  type = 'end',
  startDate,
  endDate,
  maxSelectableDate,
  value,
  onChange,
  ...sliderProps
}: TimeSliderProps) {
  const now = dayjs();

  const { rentType } = useRentTypeStore();

  const DEFAULT_TIME_RANGE: number[] = React.useMemo(() => {
    return DEFAULT_TIME_RANGE_OBJ[
      rentType as keyof typeof DEFAULT_TIME_RANGE_OBJ
    ];
  }, []);

  const minTime = now.startOf('day');
  const maxTime = React.useMemo(
    () =>
      now
        .startOf('day')
        .set(
          'hour',
          DEFAULT_TIME_RANGE[1] -
            (isFlexibleDate &&
            type === 'start' &&
            maxSelectableDate &&
            dayjs.unix(value).month() === maxSelectableDate.month() &&
            dayjs.unix(value).date() >= maxSelectableDate.date()
              ? minHourConfig
              : 0)
        ),
    [isFlexibleDate, maxSelectableDate, minHourConfig, type, value]
  );

  const stepSize = 30;

  const formatValue = (value: number) =>
    minTime.add(value * stepSize, 'minute').format('HH:mm');

  const maxSteps = maxTime.diff(minTime, 'minute') / stepSize;
  const [sliderValue, setSliderValue] = React.useState(getSeconds(value));
  const [minSteps, setMinSteps] = React.useState(
    dayjs
      .duration(
        DEFAULT_TIME_RANGE[0] + (isFlexibleDate ? minHourConfig : 0),
        'hours'
      )
      .asMinutes() / stepSize
  );

  React.useEffect(() => {
    const now = dayjs();
    const isNow = dayjs.unix(value).isSame(now, 'date');

    if (isNow) {
      if (
        isFlexibleDate &&
        startDate &&
        dayjs.unix(value).isSame(startDate, 'date')
      ) {
        setMinSteps(
          Math.round(
            startDate
              .add(minHourConfig, 'hour')
              .diff(startDate.startOf('day'), 'minute') / stepSize
          )
        );
      } else {
        const formattedStartUnix = getValidStartTime(
          now,
          timeConfig,
          DEFAULT_TIME_RANGE
        );
        setMinSteps(
          Math.round(
            dayjs.unix(formattedStartUnix).diff(minTime, 'minute') / stepSize
          )
        );
      }
    } else {
      if (
        isFlexibleDate &&
        startDate &&
        dayjs.unix(value).isSame(startDate, 'date')
      ) {
        setMinSteps(
          Math.round(
            startDate
              .add(minHourConfig, 'hour')
              .diff(startDate.startOf('day'), 'minute') / stepSize
          )
        );
      } else {
        setMinSteps(
          dayjs.duration(DEFAULT_TIME_RANGE[0], 'hours').asMinutes() / stepSize
        );
      }
    }

    setSliderValue(getSeconds(value));
  }, [timeConfig, value, startDate, isFlexibleDate]);

  const handleChange = (event: Event, newValue: number | number[]) => {
    const totalSeconds = (newValue as number) * stepSize * 60; // convert steps to seconds
    setSliderValue(totalSeconds);

    const duration = dayjs.duration(totalSeconds, 'seconds'); // Create a duration object

    const hours = duration.hours(); // Get the number of hours
    const minutes = duration.minutes(); // Get the number of minutes
    const secondsLeft = duration.seconds(); // Get the number of seconds left

    const newStartTime = dayjs
      .unix(value)
      .set('hour', hours)
      .set('minute', minutes)
      .set('second', secondsLeft);

    onChange(newStartTime.unix());
  };

  return (
    <Box
      sx={{
        px: 4.5,
      }}
    >
      <Slider
        {...sliderProps}
        value={sliderValue / stepSize / 60}
        min={minSteps}
        max={maxSteps}
        step={1}
        onChange={handleChange}
        valueLabelDisplay="on"
        valueLabelFormat={formatValue}
        sx={(theme) => ({
          position: 'relative',
          '&::before, &::after': {
            display: 'block',
            content: '""',
            width: 'calc(100% + 64px)',
            height: '2px',
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 0,
          },

          '&::before': {
            display: type === 'end' ? 'block' : 'none',
            bgcolor: 'grey.100',
            left: -32,
          },

          '&::after': {
            display: type === 'start' ? 'block' : 'none',
            bgcolor: color.primary[50],
            right: -32,
          },

          [`.${sliderClasses.track}`]: {
            left: '-32px !important',
            zIndex: 1,
            bgcolor: type === 'start' ? 'grey.100' : color.primary[50],
            opacity: 1,
          },

          [`.${sliderClasses.valueLabelOpen}`]: {
            borderColor: color.primary[50],
            color: color.neutral[90],
            ...theme.typography.body2,
            fontWeight: 700,
            top: '50%',
            transform: 'translateY(-50%) !important',
            '&::before': { display: 'none' },
          },

          [`.${sliderClasses.thumb}`]: {
            zIndex: 2,
          },

          [`.${sliderClasses.rail}`]: {
            bgcolor: type === 'end' ? 'grey.100' : color.primary[50],
            opacity: 1,
          },
        })}
      />
    </Box>
  );
}
